import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useCssHandles } from '../../../shared/hooks/useCssHandles'
import { createPixPayment, checkPaymentStatus, simulatePixPayment, confirmPaymentAndUpdateOrder } from '../services/paymentService'
import { Input } from '../../../shared/components'
import { PaymentStatus } from '../../../services'

const CSS_HANDLES = [
  'pixPayment',
  'qrCodeContainer',
  'qrCodeImage',
  'pixKey',
  'copyButton',
  'statusMessage',
  'expirationTime',
  'instructions'
] as const

// Proteção simples contra StrictMode
let isGeneratingGlobal = false

interface PixPaymentProps {
  amount: number
  customerEmail: string
  customerName: string
  orderId?: string | null
  onSuccess?: (paymentId: string) => void
  onError?: (error: string) => void
  className?: string
  // Dados de visualização (opcionais)
  existingPaymentData?: {
    pixCode?: string
    qrCodeUrl?: string
    expiresAt?: string
    paymentIntentId?: string
    status?: string
  }
}

const PixPayment: React.FC<PixPaymentProps> = ({
  amount,
  customerEmail,
  customerName,
  orderId,
  onSuccess,
  onError,
  className = '',
  existingPaymentData
}) => {
  const handles = useCssHandles(CSS_HANDLES)
  const [isLoading, setIsLoading] = useState(!existingPaymentData)
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(existingPaymentData?.qrCodeUrl || null)
  const [pixCode, setPixCode] = useState<string | null>(existingPaymentData?.pixCode || null)
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(existingPaymentData?.paymentIntentId || null)
  const [expiresAt, setExpiresAt] = useState<string | null>(existingPaymentData?.expiresAt || null)
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0)
  const [isExpired, setIsExpired] = useState(false)
  const isPaidRef = useRef(false)
  const [isPaid, setIsPaid] = useState(() => {
    const paid = existingPaymentData?.status?.toLowerCase() === PaymentStatus.PAID || 
      existingPaymentData?.status?.toLowerCase() === 'succeeded' ||
      existingPaymentData?.status?.toLowerCase() === 'confirmed' ||
      existingPaymentData?.status?.toLowerCase() === 'received'
    isPaidRef.current = paid
    return paid
  })

  // Sincronizar ref com estado
  useEffect(() => {
    isPaidRef.current = isPaid
  }, [isPaid])
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasNotifiedExpiration, setHasNotifiedExpiration] = useState(false)
  const [isSimulating, setIsSimulating] = useState(false)
  const hasGeneratedPix = useRef(!!existingPaymentData)
  const isGenerating = useRef(false)
  const onErrorRef = useRef(onError)
  const onSuccessRef = useRef(onSuccess)
  
  // Atualizar refs quando callbacks mudarem
  useEffect(() => {
    onErrorRef.current = onError
    onSuccessRef.current = onSuccess
  }, [onError, onSuccess])
  
  // Verificar se está em desenvolvimento
  const isDevelopment = process.env.NODE_ENV === 'development' || 
    process.env.REACT_APP_ENV === 'development' ||
    window.location.hostname === 'localhost'

  const generatePixPayment = useCallback(async (isRegeneration = false) => {
    // Evitar chamadas duplicadas (proteção local e global)
    if (isGenerating.current || (hasGeneratedPix.current && !isRegeneration)) {
      console.log('[PixPayment] Ignorando chamada - já está gerando ou já foi gerado')
      return
    }

    // Resetar flag global se necessário (proteção contra travamento)
    if (isGeneratingGlobal) {
      console.log('[PixPayment] Resetando flag global travada')
      isGeneratingGlobal = false
    }

    console.log('[PixPayment] Iniciando geração do PIX...', { amount, orderId, customerEmail })
    isGenerating.current = true
    isGeneratingGlobal = true
    setIsLoading(true)
    setIsExpired(false)
    setIsPaid(false)
    setHasNotifiedExpiration(false)
    setError(null)
    setCopied(false)
    setPaymentIntentId(null)

    // Timeout de segurança para resetar flags se algo der errado
    const safetyTimeout = setTimeout(() => {
      if (isGenerating.current) {
        console.warn('[PixPayment] Timeout de segurança - resetando flags')
        isGenerating.current = false
        isGeneratingGlobal = false
        setIsLoading(false)
      }
    }, 30000) // 30 segundos

    try {
      console.log('[PixPayment] Chamando createPixPayment...')
      const response = await createPixPayment({
        amount,
        orderId: orderId || undefined,
        customerEmail,
        customerName,
        description: `Compra Baterpower Baterias - ${amount.toFixed(2)}`
      })

      console.log('[PixPayment] PIX gerado com sucesso:', response)
      setQrCodeUrl(response.qrCodeUrl || null)
      setPixCode(response.pixCode)
      setPaymentIntentId(response.paymentIntentId)
      setExpiresAt(response.expiresAt)
      setRemainingSeconds(0)
      hasGeneratedPix.current = true
    } catch (err) {
      console.error('[PixPayment] Erro ao gerar PIX:', err)
      const message = err instanceof Error ? err.message : 'Erro ao gerar PIX'
      setError(message)
      onErrorRef.current?.(message)
      hasGeneratedPix.current = false
    } finally {
      clearTimeout(safetyTimeout)
      setIsLoading(false)
      isGenerating.current = false
      isGeneratingGlobal = false
      console.log('[PixPayment] Finalizado - flags resetadas')
    }
  }, [amount, customerEmail, customerName, orderId])

  const handleStatusUpdate = useCallback(
    async (status: string) => {
      const normalized = status.toLowerCase()
      if ((normalized === PaymentStatus.PAID || normalized === 'succeeded' || normalized === 'confirmed' || normalized === 'received') && paymentIntentId) {
        if (!isPaid) {
          setIsPaid(true)
          setError(null)
          
          // Confirmar pagamento e atualizar pedido no backend
          // Em desenvolvimento, pode falhar se o pagamento ainda não estiver disponível no Asaas
          // Mas não devemos exibir erro ao usuário, pois o pagamento já foi confirmado
          try {
            await confirmPaymentAndUpdateOrder(paymentIntentId)
          } catch (err) {
            // Silenciosamente logar o erro sem exibir toast
            // O webhook pode atualizar o pedido depois, ou o pagamento já foi processado
            console.warn('[PixPayment] Erro ao confirmar pagamento no backend (não crítico):', err)
            // Não chamar onError aqui, pois o pagamento foi confirmado com sucesso
          }
          
          // Sempre chamar onSuccess, mesmo se confirmPaymentAndUpdateOrder falhar
          // pois o pagamento já foi confirmado pelo gateway
          onSuccessRef.current?.(paymentIntentId)
        }
      } else if (
        normalized === PaymentStatus.CANCELLED ||
        normalized === PaymentStatus.CANCELED ||
        normalized === 'requires_payment_method' ||
        normalized === 'overdue' ||
        normalized === 'failed'
      ) {
        const message = 'Pagamento PIX cancelado. Gere um novo código.'
        setError(message)
        onErrorRef.current?.(message)
        setIsExpired(true)
        setPaymentIntentId(null)
      }
    },
    [isPaid, onError, onSuccess, paymentIntentId]
  )

  const checkStatus = useCallback(async () => {
    if (!paymentIntentId || isPaid) return null

    try {
      const statusResponse = await checkPaymentStatus(paymentIntentId)
      handleStatusUpdate(statusResponse.status)
      return statusResponse.status
    } catch (err) {
      // Não exibir erro se o pagamento já foi confirmado
      // Apenas logar silenciosamente
      console.warn('[PixPayment] Erro ao consultar status PIX (não crítico):', err)
      return null
    }
  }, [handleStatusUpdate, paymentIntentId, isPaid])

  const handleCopyPixKey = async () => {
    if (!pixCode) return

    try {
      await navigator.clipboard.writeText(pixCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 3000)
    } catch (err) {
      console.error('Erro ao copiar:', err)
    }
  }


  useEffect(() => {
    console.log('[PixPayment] useEffect executado', { 
      hasGeneratedPix: hasGeneratedPix.current, 
      isGenerating: isGenerating.current, 
      isGeneratingGlobal 
    })

    // Resetar flag global se necessário (proteção contra travamento)
    if (isGeneratingGlobal && !isGenerating.current) {
      console.log('[PixPayment] Resetando flag global travada no useEffect')
      isGeneratingGlobal = false
    }

    // Executar apenas uma vez na montagem
    if (!hasGeneratedPix.current && !isGenerating.current && !isGeneratingGlobal) {
      console.log('[PixPayment] Chamando generatePixPayment...')
      // Gerar PIX imediatamente, mesmo sem orderId
      // O orderId pode ser atualizado depois e a transação será associada
      void generatePixPayment()
    } else {
      console.log('[PixPayment] Não chamando generatePixPayment - condições não atendidas')
    }
  }, [generatePixPayment])

  useEffect(() => {
    if (!expiresAt || isPaidRef.current) return

    const updateRemainingTime = () => {
      try {
        if (isPaidRef.current) return

        const expirationTime = new Date(expiresAt).getTime()
        if (isNaN(expirationTime)) {
          console.warn('[PixPayment] Data de expiração inválida:', expiresAt)
          return
        }

        const now = Date.now()
        const diff = Math.floor((expirationTime - now) / 1000)

        // Se a diferença for negativa mas muito pequena (menos de 60s), 
        // damos uma tolerância para evitar flash de "expirado" por drift de relógio
        if (diff <= 0) {
          if (Math.abs(diff) < 60 && !hasNotifiedExpiration) {
            setRemainingSeconds(1)
            return
          }
          
          setRemainingSeconds(0)
          setIsExpired(true)
          setPaymentIntentId(null)
          if (!hasNotifiedExpiration) {
            setHasNotifiedExpiration(true)
            onError?.('O tempo do PIX expirou. Gere um novo código para continuar.')
          }
          return
        }

        setRemainingSeconds(diff)
      } catch (err) {
        console.error('[PixPayment] Erro ao calcular tempo restante:', err)
      }
    }

    updateRemainingTime()
    const interval = setInterval(updateRemainingTime, 1000)
    return () => clearInterval(interval)
  }, [expiresAt, hasNotifiedExpiration, onError])

  useEffect(() => {
    if (!paymentIntentId || isExpired || isPaid) {
      return
    }

    const interval = window.setInterval(() => {
      void checkStatus()
    }, 5000)

    return () => window.clearInterval(interval)
  }, [checkStatus, isExpired, isPaid, paymentIntentId])

  useEffect(() => {
    if (!paymentIntentId) return
    void checkStatus()
  }, [checkStatus, paymentIntentId])

  const formattedRemainingTime = useMemo(() => {
    if (remainingSeconds <= 0) return '00:00'
    
    const hours = Math.floor(remainingSeconds / 3600)
    const minutes = Math.floor((remainingSeconds % 3600) / 60)
    const seconds = remainingSeconds % 60
    
    if (hours > 0) {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    }
    
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  }, [remainingSeconds])

  const handleSimulatePayment = useCallback(async () => {
    if (!paymentIntentId || isPaid || isSimulating) return

    setIsSimulating(true)
    setError(null)

    try {
      await simulatePixPayment(paymentIntentId)
      // Aguardar um pouco e verificar o status
      setTimeout(() => {
        if (paymentIntentId && !isPaid) {
          checkPaymentStatus(paymentIntentId)
            .then((status) => {
              handleStatusUpdate(status.status)
            })
            .catch((err) => {
              // Não exibir erro se o pagamento já foi confirmado
              // Apenas logar silenciosamente
              console.warn('[PixPayment] Erro ao verificar status após simulação (não crítico):', err)
            })
        }
      }, 1000)
    } catch (err) {
      // Só exibir erro se realmente falhou a simulação
      // Não exibir se o pagamento já foi confirmado
      if (!isPaid) {
        const message = err instanceof Error ? err.message : 'Erro ao simular pagamento'
        setError(message)
        onError?.(message)
      } else {
        console.warn('[PixPayment] Erro ao simular pagamento (mas pagamento já confirmado):', err)
      }
    } finally {
      setIsSimulating(false)
    }
  }, [paymentIntentId, isPaid, isSimulating, onError, handleStatusUpdate])

  if (isLoading) {
    return (
      <div className={`${handles.pixPayment} ${className} text-center py-8`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Gerando código PIX...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`${handles.pixPayment} ${className}`}>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600">
          <p className="font-medium mb-2">Erro ao gerar PIX</p>
          <p className="text-sm">{error}</p>
          <button
            onClick={() => generatePixPayment(true)}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={`${handles.pixPayment} ${className}`}>
      <div className={`${handles.qrCodeContainer} bg-white border border-gray-200 rounded-lg p-8 mb-6`}>
        <div className="text-center">
          {qrCodeUrl ? (
            <div className={`${handles.qrCodeImage} inline-block p-4 bg-white border-2 border-gray-300 rounded-lg`}>
              <img 
                src={qrCodeUrl} 
                alt="QR Code PIX" 
                className="w-64 h-64 mx-auto"
              />
            </div>
          ) : (
            <div className="w-64 h-64 mx-auto bg-gray-100 rounded-lg flex items-center justify-center">
              <p className="text-gray-500">QR Code indisponível</p>
            </div>
          )}
          
          {/* Botão de simular pagamento (apenas em desenvolvimento) */}
          {isDevelopment && paymentIntentId && !isPaid && !isExpired && (
            <div className="mt-4">
              <button
                onClick={handleSimulatePayment}
                disabled={isSimulating}
                className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors text-sm font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isSimulating ? (
                  <>
                    <span className="inline-block animate-spin mr-2">⏳</span>
                    Simulando...
                  </>
                ) : (
                  <>
                    🧪 Simular Pagamento (Dev)
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="mb-4">
        <div className="flex gap-2">
          <Input
            type="text"
            value={pixCode || ''}
            readOnly
            className={`${handles.pixKey} flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm font-mono`}
          />
          <button
            onClick={handleCopyPixKey}
            className={`${handles.copyButton} px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap`}
          >
            {copied ? '✓ Copiado!' : 'Copiar'}
          </button>
        </div>
      </div>

      {expiresAt && !isPaid && (
        <div className={`${handles.expirationTime} text-center text-sm text-gray-600 mb-4`}>
          <p className="font-medium text-gray-900 mb-1">
            {isExpired ? 'Código expirado' : 'Tempo para pagamento:'}
          </p>
          {!isExpired && (
            <p className="text-2xl font-bold text-blue-600 font-mono">
              {formattedRemainingTime}
            </p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            O código expira em 60 minutos
          </p>
        </div>
      )}

      {isPaid ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
          <p className="font-medium text-green-800">✓ Pagamento confirmado!</p>
        </div>
      ) : isExpired ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center mb-4">
          <p className="font-medium text-red-700 mb-2">Código PIX expirado</p>
          <button
            onClick={() => generatePixPayment(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            Gerar novo código
          </button>
        </div>
      ) : (
        <div className={`${handles.statusMessage} bg-blue-50 border border-blue-200 rounded-lg p-4 text-center`}>
          <div className="flex items-center justify-center">
            <div className="animate-pulse w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
            <p className="text-sm text-blue-900">Aguardando confirmação do pagamento...</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default PixPayment

